import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import {
  ArrowRight, Stamp, Lock, Sparkles, Link2, Shield, GitMerge, Users, Activity, BarChart3,
  FileText, PenTool, FolderLock, Cpu, CheckCircle2, XCircle, Star, Zap, Play,
  ChevronLeft, ChevronRight as ChevronRightIcon, Award, Globe2, BadgeCheck, Fingerprint,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLocale } from "@/contexts/LocaleContext";
import heroBg from "@/assets/hero-bg.jpg";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from "recharts";

/* ── Hooks ── */
function useIsMobile() {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return mobile;
}

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function useCountUp(target: number, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);
  return { count, ref };
}

/* ── Subcomponents ── */
const ChartReveal = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  const { ref, visible } = useInView(0.15);
  return (
    <div ref={ref} className={className}>
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={visible ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 30, scale: 0.96 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {visible ? children : <div style={{ minHeight: 280 }} />}
      </motion.div>
    </div>
  );
};

/* Animated floating particles for Hero */
const HeroParticles = () => {
  const particles = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 2 + Math.random() * 4,
    duration: 8 + Math.random() * 12,
    delay: Math.random() * 5,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-seal-gold/30"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

/* Trust logos */
const TRUST_LOGOS = [
  { name: "中建国际", icon: "🏗️" },
  { name: "华为", icon: "📱" },
  { name: "锦天城", icon: "⚖️" },
  { name: "中信银行", icon: "🏦" },
  { name: "腾讯", icon: "💬" },
  { name: "平安集团", icon: "🛡️" },
  { name: "字节跳动", icon: "🎵" },
  { name: "大疆", icon: "🚁" },
];

const statValues = [
  { value: 1280000, suffix: "+", prefix: "", format: true },
  { value: 3200, suffix: "+", prefix: "", format: true },
  { value: 99.6, suffix: "%", prefix: "", format: false },
  { value: 8500000, suffix: "+", prefix: "", format: true },
];

const StatCard = ({ stat, label, index }: { stat: typeof statValues[0]; label: string; index: number }) => {
  const { count, ref } = useCountUp(
    stat.format ? stat.value : Math.floor(stat.value * 10),
    1800 + index * 200
  );
  const displayValue = stat.format
    ? (count >= 1000000 ? `${(count / 10000).toFixed(0)} 万` : count >= 10000 ? `${(count / 10000).toFixed(1)} 万` : count.toLocaleString())
    : (count / 10).toFixed(1);

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="text-center py-4">
      <p className="text-2xl md:text-3xl font-display font-bold text-gradient-gold">{stat.prefix}{displayValue}{stat.suffix}</p>
      <p className="text-[11px] text-primary-foreground/50 mt-1">{label}</p>
    </motion.div>
  );
};

const CHART_COLORS = [
  "hsl(var(--accent))",
  "hsl(var(--primary))",
  "hsl(var(--seal-gold, 43 74% 58%))",
  "hsl(var(--chart-4, 280 65% 60%))",
  "hsl(var(--chart-5, 160 60% 45%))",
  "hsl(var(--destructive))",
];

/* ── Main Component ── */
const Index = () => {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const chartH = isMobile ? 260 : 360;
  const tickSize = isMobile ? 9 : 11;
  const chartMargin = isMobile ? { top: 5, right: 10, left: -15, bottom: 5 } : { top: 10, right: 30, left: 0, bottom: 10 };
  const [activeDemo, setActiveDemo] = useState(0);
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Show floating CTA after scrolling past hero
  useEffect(() => {
    const onScroll = () => setShowFloatingCta(window.scrollY > window.innerHeight * 0.8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Chart data
  const featureRadarData = t.homeFeatures.items.map((item, i) => ({
    subject: item.title.slice(0, 4),
    DocSeal: [98, 96, 99, 95, 97, 99][i] || 90,
    industry: [55, 50, 40, 35, 45, 30][i] || 50,
  }));
  const perfData = t.featuresCharts.perfMetrics;
  const productPieData = [
    { name: t.homeProducts.items[0]?.title || "Smart Packet", value: 45 },
    { name: t.homeProducts.items[1]?.title || "SealFlow", value: 35 },
    { name: t.homeProducts.items[2]?.title || "DataRoom", value: 20 },
  ];
  const growthData = [
    { month: "Q1'24", docs: 120, seals: 45, users: 800 },
    { month: "Q2'24", docs: 280, seals: 120, users: 1600 },
    { month: "Q3'24", docs: 520, seals: 280, users: 2800 },
    { month: "Q4'24", docs: 780, seals: 450, users: 4200 },
    { month: "Q1'25", docs: 1080, seals: 680, users: 6500 },
    { month: "Q2'25", docs: 1500, seals: 950, users: 9200 },
  ];
  const securityDimensions = t.featuresCharts.secDimensions;
  const securityRadarData = securityDimensions.map((dim, i) => ({
    subject: dim,
    DocSeal: [98, 95, 97, 99, 98, 96][i] || 95,
    traditional: [60, 50, 55, 40, 35, 45][i] || 45,
  }));
  const compData = t.featuresCharts.compFeatures;

  // Demo features for product showcase
  const demoIcons = [Sparkles, Link2, PenTool, BarChart3];
  const demoFeatures = t.demo.features.map((f, i) => ({ ...f, icon: demoIcons[i] }));

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* ══════════ HERO ══════════ */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden" style={{ backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        {/* Animated background */}
        <div className="absolute inset-0">
          <HeroParticles />
          <motion.div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-seal-gold/8 blur-[150px]"
            animate={{ scale: [1, 1.2, 1], opacity: [0.06, 0.12, 0.06] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
          <motion.div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full bg-accent/5 blur-[180px]"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.04, 0.1, 0.04] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
        </div>

        <div className="container relative pt-28 pb-16 md:pt-32 md:pb-20">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl mx-auto text-center">
            {/* Badge with pulse */}
            <a href="https://www.unwbo.org" target="_blank" rel="noopener noreferrer">
              <motion.div className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full border border-seal-gold/30 bg-seal-gold/10 backdrop-blur-sm mb-6 cursor-pointer hover:bg-seal-gold/20 transition-colors"
                animate={{ boxShadow: ["0 0 0 0 rgba(212,175,55,0)", "0 0 0 8px rgba(212,175,55,0.1)", "0 0 0 0 rgba(212,175,55,0)"] }}
                transition={{ duration: 2.5, repeat: Infinity }}>
                <div className="relative flex items-center justify-center w-5 h-5">
                  <Globe2 className="w-5 h-5 text-seal-gold" />
                  <Shield className="w-2.5 h-2.5 text-seal-gold absolute -bottom-0.5 -right-0.5" />
                </div>
                <span className="text-xs font-semibold text-seal-gold tracking-wide">{t.hero.badge}</span>
                <BadgeCheck className="w-4 h-4 text-seal-gold" />
              </motion.div>
            </a>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] font-display font-bold tracking-tight text-primary-foreground leading-[1.08] mb-5">
              <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                {t.hero.title1}
              </motion.span>
              <br />
              <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="text-gradient-gold inline-block">
                {t.hero.title2}
              </motion.span>
            </h1>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="text-2xl md:text-4xl lg:text-5xl font-display font-bold tracking-[0.15em] text-primary-foreground/80 max-w-3xl mx-auto mb-10 leading-relaxed flex items-center justify-center flex-wrap gap-2 md:gap-3">
              {t.hero.subtitle.split('➔').map((word: string, i: number, arr: string[]) => (
                <span key={i} className="flex items-center gap-2 md:gap-3">
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + i * 0.15 }}
                    className="inline-block"
                  >
                    {word.trim()}
                  </motion.span>
                  {i < arr.length - 1 && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.85 + i * 0.15, type: "spring", stiffness: 300 }}
                      className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 animate-[pulse_2s_ease-in-out_infinite]"
                    >
                      ➔
                    </motion.span>
                  )}
                </span>
              ))}
            </motion.div>

            {/* CTA Group */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <Button variant="hero" size="lg" className="relative group text-base px-8 py-6" asChild>
                <Link to="/dashboard">
                  <span className="relative z-10 flex items-center gap-2">
                    {t.hero.cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
              <Button variant="hero-outline" size="lg" className="text-base px-8 py-6" asChild>
                <Link to="/features">
                  <Play className="w-4 h-4 mr-1" />
                  {t.hero.ctaSecondary}
                </Link>
              </Button>
            </motion.div>

            {/* Trust indicators inline */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}
              className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-primary-foreground/40">
              <div className="flex items-center gap-1.5 text-xs">
                <Shield className="w-3.5 h-3.5 text-seal-gold/60" />
                <span>端到端加密</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <BadgeCheck className="w-3.5 h-3.5 text-seal-gold/60" />
                <span>司法级存证</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <Globe2 className="w-3.5 h-3.5 text-seal-gold/60" />
                <span>3,200+ 企业信赖</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <Award className="w-3.5 h-3.5 text-seal-gold/60" />
                <span>ISO 27001</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 8, 0], opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}>
            <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/20 flex items-start justify-center p-1.5">
              <div className="w-1.5 h-3 rounded-full bg-seal-gold/50" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════ TRUST LOGOS MARQUEE ══════════ */}
      <section className="py-8 md:py-12 bg-background border-b border-border overflow-hidden">
        <div className="container">
          <p className="text-center text-[11px] text-muted-foreground font-medium tracking-widest uppercase mb-6">他们都在使用 DocSeal</p>
          <div className="relative overflow-hidden">
            <motion.div className="flex gap-8 md:gap-12"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
              {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
                <div key={i} className="flex items-center gap-2 shrink-0 px-4 py-2 rounded-lg bg-muted/30">
                  <span className="text-lg">{logo.icon}</span>
                  <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{logo.name}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════ PRODUCT DEMO SHOWCASE ══════════ */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.demo.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.demo.heading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">{t.demo.subheading}</p>
          </motion.div>

          {/* Tabbed demo */}
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-center gap-2 mb-8">
              {demoFeatures.map((f, i) => (
                <button key={i} onClick={() => setActiveDemo(i)}
                  className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-xl text-xs md:text-sm font-medium transition-all ${
                    activeDemo === i
                      ? "bg-accent text-accent-foreground shadow-lg shadow-accent/20"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                  }`}>
                  <f.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{f.title}</span>
                  <span className="sm:hidden">{f.title.slice(0, 4)}</span>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={activeDemo} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}
                className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
                {/* Demo visual */}
                <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card relative overflow-hidden min-h-[360px]">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-[60px]" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full blur-[40px]" />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                        {(() => { const Icon = demoFeatures[activeDemo].icon; return <Icon className="w-6 h-6 text-accent" />; })()}
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-lg">{demoFeatures[activeDemo].title}</h3>
                        <p className="text-xs text-muted-foreground">DocSeal 核心功能</p>
                      </div>
                    </div>

                    {/* ── Tab 0: Smart Generate ── */}
                    {activeDemo === 0 && (
                      <div className="space-y-3">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 mb-4">
                          <div className="h-8 w-8 rounded bg-accent/10 flex items-center justify-center"><FileText className="w-4 h-4 text-accent" /></div>
                          <span className="text-xs font-medium">{t.demo.simLabels.generating}</span>
                        </motion.div>
                        {t.demo.simLabels.fields.map((field, i) => (
                          <motion.div key={field} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.12 }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                            <Sparkles className="w-4 h-4 text-accent shrink-0" />
                            <span className="text-xs font-medium w-16 shrink-0">{field}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-accent/15 overflow-hidden">
                              <motion.div className="h-full bg-accent/60 rounded-full" initial={{ width: 0 }} animate={{ width: `${70 + i * 8}%` }}
                                transition={{ duration: 1, delay: 0.4 + i * 0.15 }} />
                            </div>
                            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 + i * 0.15 }}
                              className="text-[10px] text-accent font-semibold">{t.demo.simLabels.filled}</motion.span>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {/* ── Tab 1: Dynamic Distribution ── */}
                    {activeDemo === 1 && (
                      <div className="space-y-3">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-lg border border-border p-3 mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium">Smart Link #SL-2024</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 font-medium">{t.demo.simLabels.linkActive}</span>
                          </div>
                          <div className="flex gap-4 text-[11px] text-muted-foreground">
                            {t.demo.simLabels.linkSecurity.map((s) => <span key={s}>{s}</span>)}
                          </div>
                        </motion.div>
                        {[{ name: "A", time: "2m", pagesRead: 12, totalPages: 15, heat: 80 },
                          { name: "B", time: "15m", pagesRead: 8, totalPages: 15, heat: 53 },
                          { name: "C", time: "1h", pagesRead: 15, totalPages: 15, heat: 100 }].map((v, i) => (
                          <motion.div key={v.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                            <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-xs font-bold text-accent">{v.name}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{v.name}</span>
                                <span className="text-[10px] text-muted-foreground">{v.time}</span>
                              </div>
                              <span className="text-[10px] text-muted-foreground">{t.demo.simLabels.read} {v.pagesRead}/{v.totalPages} {t.demo.simLabels.pages}</span>
                            </div>
                            <div className="w-12 h-2 rounded-full bg-accent/15 overflow-hidden">
                              <motion.div className="h-full rounded-full" style={{ background: v.heat === 100 ? 'hsl(var(--accent))' : v.heat > 60 ? 'hsl(var(--accent) / 0.7)' : 'hsl(var(--accent) / 0.4)' }}
                                initial={{ width: 0 }} animate={{ width: `${v.heat}%` }} transition={{ duration: 1, delay: 0.5 + i * 0.15 }} />
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {/* ── Tab 2: Online Signing ── */}
                    {activeDemo === 2 && (
                      <div className="space-y-3">
                        {t.demo.simLabels.roles.map((step, i) => {
                          const status = i < 2 ? 'completed' : i === 2 ? 'active' : 'pending';
                          return (
                          <motion.div key={step.role} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.15 }}
                            className={`flex items-center gap-3 p-3 rounded-lg ${status === 'active' ? 'bg-accent/10 border border-accent/30' : 'bg-muted/30'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              status === 'completed' ? 'bg-green-500/10' : status === 'active' ? 'bg-accent/20' : 'bg-muted'
                            }`}>
                              {status === 'completed' ? <CheckCircle2 className="w-4 h-4 text-green-600" /> :
                               status === 'active' ? <PenTool className="w-4 h-4 text-accent animate-pulse" /> :
                               <Lock className="w-4 h-4 text-muted-foreground" />}
                            </div>
                            <div className="flex-1">
                              <span className="text-xs font-medium">{step.role}</span>
                              <p className="text-[10px] text-muted-foreground">{step.name}</p>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              status === 'completed' ? 'bg-green-500/10 text-green-600' :
                              status === 'active' ? 'bg-accent/10 text-accent' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {status === 'completed' ? t.demo.simLabels.signed : status === 'active' ? t.demo.simLabels.signing : t.demo.simLabels.pending}
                            </span>
                          </motion.div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Tab 3: Smart Settlement ── */}
                    {activeDemo === 3 && (
                      <div className="space-y-3">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 gap-2 mb-3">
                          {[{ label: t.demo.simLabels.totalReceivable, value: "¥1,280,000" }, { label: t.demo.simLabels.settled, value: "¥960,000" }].map((s) => (
                            <div key={s.label} className="rounded-lg bg-muted/30 p-3 text-center">
                              <p className="text-[10px] text-muted-foreground">{s.label}</p>
                              <p className="text-sm font-display font-bold mt-0.5">{s.value}</p>
                            </div>
                          ))}
                        </motion.div>
                        {[{ doc: "#PO-2024-086", matchIdx: 0, score: 98 },
                          { doc: "#SA-2024-041", matchIdx: 1, score: 95 },
                          { doc: "#FA-2024-012", matchIdx: 2, score: 100 }].map((item, i) => (
                          <motion.div key={item.doc} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                            <BarChart3 className="w-4 h-4 text-accent shrink-0" />
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-medium block truncate">{item.doc}</span>
                              <span className="text-[10px] text-muted-foreground">{t.demo.simLabels.match[item.matchIdx]}</span>
                            </div>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 font-semibold">{item.score}%</span>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Demo info */}
                <div className="space-y-6">
                  <p className="text-sm text-muted-foreground leading-relaxed">{demoFeatures[activeDemo].desc}</p>
                  <div className="rounded-xl border border-accent/20 bg-accent/5 p-5">
                    <p className="text-3xl md:text-4xl font-display font-bold text-accent">{demoFeatures[activeDemo].metric}</p>
                    <p className="text-xs text-muted-foreground mt-1">{demoFeatures[activeDemo].metricLabel}</p>
                  </div>
                  <Button variant="hero" size="lg" className="w-full sm:w-auto" asChild>
                    <Link to="/dashboard">
                      {t.demo.cta} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>


      {/* ══════════ FEATURE RADAR ══════════ */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.homeFeatures.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.homeFeatures.heading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">{t.homeFeatures.subheading}</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto items-center">
            <ChartReveal className="rounded-2xl border border-border bg-card p-4 md:p-6 shadow-card">
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 360}>
                <RadarChart data={featureRadarData} cx="50%" cy="50%" outerRadius={isMobile ? "65%" : "80%"}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: isMobile ? 9 : 11, fill: "hsl(var(--muted-foreground))" }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar name="DocSeal" dataKey="DocSeal" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.25} strokeWidth={2} isAnimationActive animationDuration={1500} />
                  <Radar name={t.featuresCharts.traditionalLabel} dataKey="industry" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground))" fillOpacity={0.1} strokeWidth={1} strokeDasharray="4 4" isAnimationActive animationDuration={1500} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartReveal>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4">
              {t.homeFeatures.items.map((item, i) => {
                const icons = [Stamp, PenTool, Shield, Activity, Sparkles, Lock];
                const Icon = icons[i % icons.length];
                return (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-border bg-card/60 hover:bg-card transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>



      {/* ══════════ PRODUCT PIE + CARDS ══════════ */}
      <section className="py-10 md:py-14 bg-background">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.homeProducts.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.homeProducts.heading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">{t.homeProducts.subheading}</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto items-center">
            <ChartReveal className="rounded-2xl border border-border bg-card p-4 md:p-6 shadow-card">
              <ResponsiveContainer width="100%" height={isMobile ? 240 : 320}>
                <PieChart>
                  <Pie data={productPieData} cx="50%" cy="50%" innerRadius={isMobile ? 45 : 70} outerRadius={isMobile ? 80 : 120} dataKey="value" label={isMobile ? false : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} isAnimationActive animationDuration={1200}>
                    {productPieData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i]} stroke="hsl(var(--card))" strokeWidth={3} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  {isMobile && <Legend wrapperStyle={{ fontSize: 10 }} />}
                </PieChart>
              </ResponsiveContainer>
            </ChartReveal>
            <div className="space-y-4 md:space-y-5">
              {t.homeProducts.items.map((product, i) => {
                const icons = [FileText, PenTool, FolderLock];
                const Icon = icons[i % icons.length];
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4 p-5 rounded-xl border border-border bg-card shadow-card hover:shadow-card-hover transition-all">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: CHART_COLORS[i], opacity: 0.15 }}>
                      <Icon className="w-5 h-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display font-bold text-base mb-1">{product.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-2">{product.desc}</p>
                      <Link to="/features" className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:text-accent/80 transition-colors">
                        {product.cta} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ AI CAPABILITIES: Templates + Acquisition ══════════ */}
      <section className="py-10 md:py-14 bg-muted/30 overflow-hidden">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">AI 能力矩阵</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">180+ 行业模板 × AI 智能引擎</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">海量专业模板与 AI 深度融合，从文档智能生成到精准获客，全链路加速您的商业运营</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* AI Doc Generation Card */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card hover:shadow-card-hover transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-accent/5 rounded-full blur-[80px]" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg">AI 智能文档生成</h3>
                    <p className="text-xs text-muted-foreground">秒级产出专业级商业文档</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    { emoji: "📑", text: "180+ 内置行业模板，覆盖法律、金融、科技、医疗等 20+ 行业" },
                    { emoji: "📁", text: "支持上传自定义模板，AI 基于您的企业模板智能生成" },
                    { emoji: "🌐", text: "8 语种智能生成，一键产出中英双语合同、协议" },
                    { emoji: "⚡", text: "从 NDA 到投标书，15+ 文档类型全覆盖" },
                  ].map((item, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-3 text-sm">
                      <span className="text-base shrink-0">{item.emoji}</span>
                      <span className="text-xs text-muted-foreground leading-relaxed">{item.text}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {["合同", "NDA", "商业计划书", "标书", "MOU", "SOW", "协议", "工作说明书"].map(tag => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-accent/8 border border-accent/15 text-[10px] font-medium text-accent">{tag}</span>
                  ))}
                </div>

                <Button variant="hero" size="sm" asChild>
                  <Link to="/ai-doc-gen">
                    开始生成 <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* AI Acquisition Card */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card hover:shadow-card-hover transition-all group relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-accent/5 rounded-full blur-[80px]" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg">AI 智能获客引擎</h3>
                    <p className="text-xs text-muted-foreground">全链路增长自动化</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    { emoji: "🔍", text: "全域网络爬取，实时捕获目标客户意图信号" },
                    { emoji: "🎯", text: "AI 身份打标与 Persona 过滤，精准锁定决策者" },
                    { emoji: "📧", text: "LLM 驱动个性化邮件，结合文档 SmartLink 追踪互动" },
                    { emoji: "📊", text: "瀑布式数据补全，自动丰富联系人信息" },
                  ].map((item, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-3 text-sm">
                      <span className="text-base shrink-0">{item.emoji}</span>
                      <span className="text-xs text-muted-foreground leading-relaxed">{item.text}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: "信号捕获", value: "实时" },
                    { label: "邮件送达率", value: "98%+" },
                    { label: "获客成本", value: "↓ 70%" },
                  ].map((m, i) => (
                    <div key={i} className="rounded-lg bg-muted/50 p-2.5 text-center">
                      <p className="text-sm font-display font-bold text-accent">{m.value}</p>
                      <p className="text-[10px] text-muted-foreground">{m.label}</p>
                    </div>
                  ))}
                </div>

                <Button variant="hero" size="sm" asChild>
                  <Link to="/dashboard?tab=acquisition">
                    开始获客 <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════ WAIO ID ══════════ */}
      <section className="py-10 md:py-14 bg-background relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-accent/5 blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-primary/5 blur-[100px]" />
        <div className="container relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">WAIO Identity</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">WAIO ID · 全球统一数字身份</h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-sm">每位用户注册即获得唯一 WAIO ID，构建跨平台、可溯源、分级信任的数字身份体系。</p>
          </motion.div>

          {/* WAIO ID Visual + Features */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto items-center mb-10">
            {/* Left: Visual */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-[60px]" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Fingerprint className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg">WAIO ID 数字身份</h3>
                    <p className="text-xs text-muted-foreground">唯一 · 可信 · 可溯源</p>
                  </div>
                </div>

                {/* Mock WAIO ID Card */}
                <div className="rounded-xl border border-accent/20 bg-gradient-to-br from-card to-accent/5 p-5 mb-5">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[10px] font-semibold tracking-widest text-accent uppercase">WAIO Digital Identity</span>
                    <BadgeCheck className="w-5 h-5 text-accent" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-12">ID</span>
                      <motion.span initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
                        className="font-mono text-sm font-bold tracking-wider text-foreground">WAIO-8A3F7C2D1E9B</motion.span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-12">级别</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent font-semibold">企业认证</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground w-12">信用</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <motion.div className="h-full bg-accent rounded-full" initial={{ width: 0 }} whileInView={{ width: "92%" }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.5 }} />
                      </div>
                      <span className="text-[10px] font-semibold text-accent">92</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "注册用户", value: "50K+" },
                    { label: "认证通过率", value: "99.8%" },
                    { label: "操作可追溯", value: "100%" },
                  ].map((m, i) => (
                    <div key={i} className="rounded-lg bg-muted/50 p-2.5 text-center">
                      <p className="text-sm font-display font-bold text-accent">{m.value}</p>
                      <p className="text-[10px] text-muted-foreground">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right: Feature list */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4">
              {[
                { icon: Fingerprint, title: "全球唯一身份标识", desc: "注册即自动生成 WAIO-XXXXXXXXXXXX 格式的唯一 ID，跨平台统一身份，替代传统分散的账号体系。" },
                { icon: Shield, title: "三级信任分层", desc: "普通用户 → 实名认证 → 政企客户，逐级提升权限与可信度，实现差异化的功能与额度管理。" },
                { icon: Users, title: "授权代表与委托核验", desc: "企业客户支持授权代表机制，上传委托书与身份材料，管理员审核后代表企业行使文档操作权。" },
                { icon: Activity, title: "全链路操作审计", desc: "每一次文档操作均绑定 WAIO ID 记录，形成不可篡改的操作日志，支持按 ID 查询完整历史轨迹。" },
                { icon: Award, title: "信用评分体系", desc: "基于操作频次、认证等级和合规表现动态计算信用评分，为合作伙伴提供可信度参考依据。" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-4 p-4 rounded-xl border border-border bg-card/60 hover:bg-card transition-colors">
                    <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          <div className="text-center">
            <Button variant="hero" size="lg" asChild>
              <Link to="/dashboard">
                立即获取 WAIO ID <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ══════════ SECURITY RADAR ══════════ */}
      <section className="py-10 md:py-14 bg-muted/30">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.featuresCharts.secLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.featuresCharts.secHeading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">{t.featuresCharts.secSubheading}</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto items-center">
            <ChartReveal className="rounded-2xl border border-border bg-card p-4 md:p-6 shadow-card">
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 360}>
                <RadarChart data={securityRadarData} cx="50%" cy="50%" outerRadius={isMobile ? "65%" : "80%"}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: isMobile ? 9 : 11, fill: "hsl(var(--muted-foreground))" }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar name={t.featuresCharts.docSealLabel} dataKey="DocSeal" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.3} strokeWidth={2} isAnimationActive animationDuration={1500} />
                  <Radar name={t.featuresCharts.traditionalLabel} dataKey="traditional" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.1} strokeWidth={1} strokeDasharray="4 4" isAnimationActive animationDuration={1500} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartReveal>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="px-4 md:px-5 py-3 border-b border-border">
                  <h3 className="font-display font-semibold text-sm">{t.featuresCharts.compHeading}</h3>
                </div>
                <div className="divide-y divide-border">
                  {compData.map((row, i) => (
                    <div key={i} className="flex items-center px-3 md:px-5 py-2.5 md:py-3 text-sm">
                      <span className="flex-1 text-[11px] md:text-xs font-medium">{row.feature}</span>
                      <span className="w-14 md:w-20 text-center">
                        {row.docseal ? <CheckCircle2 className="w-4 h-4 text-accent mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground/30 mx-auto" />}
                      </span>
                      <span className="w-14 md:w-20 text-center">
                        {row.traditional ? <CheckCircle2 className="w-4 h-4 text-muted-foreground mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground/30 mx-auto" />}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center px-3 md:px-5 py-2 bg-muted/30 text-[10px] text-muted-foreground font-semibold">
                  <span className="flex-1" />
                  <span className="w-14 md:w-20 text-center">{t.featuresCharts.docSealLabel}</span>
                  <span className="w-14 md:w-20 text-center">{t.featuresCharts.traditionalLabel}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* ══════════ CORE TECH ══════════ */}
      <section className="py-10 md:py-14 bg-hero text-primary-foreground overflow-hidden">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-seal-gold-light">{t.homeTech.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-3 text-primary-foreground">{t.homeTech.heading}</h2>
            <p className="text-primary-foreground/50 max-w-md mx-auto text-sm">{t.homeTech.subheading}</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 max-w-5xl mx-auto mb-10">
            {t.homeTech.items.map((item, i) => {
              const techIcons = [Lock, Cpu, Shield, FileText];
              const Icon = techIcons[i % techIcons.length];
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="rounded-xl border border-primary-foreground/10 bg-primary-foreground/5 backdrop-blur p-5 md:p-6 text-center hover:bg-primary-foreground/10 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-seal-gold/15 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-5 h-5 text-seal-gold" />
                  </div>
                  <h3 className="font-display font-bold text-xs md:text-sm text-primary-foreground mb-1">{item.title}</h3>
                  <p className="text-[10px] md:text-[11px] text-primary-foreground/50">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center">
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/technology">{t.homeTech.cta} <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ══════════ STATS ══════════ */}
      <section className="py-10 md:py-14 bg-muted/30 overflow-hidden">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.stats.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-3">{t.stats.heading}</h2>
            <p className="text-muted-foreground max-w-md mx-auto text-sm">{t.stats.subheading}</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {statValues.map((stat, i) => (
              <StatCard key={i} stat={stat} label={t.stats.items[i]?.label || ""} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ CUSTOMER TESTIMONIALS ══════════ */}
      <section className="py-10 md:py-14 bg-muted/30">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12 md:mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.cases.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.cases.heading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">{t.cases.subheading}</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {t.cases.items.map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-border bg-card p-6 shadow-card hover:shadow-card-hover transition-all group">
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-3.5 h-3.5 fill-seal-gold text-seal-gold" />
                  ))}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed mb-6 italic">"{c.quote}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <p className="text-xs font-semibold">{c.person}</p>
                    <p className="text-[11px] text-muted-foreground">{c.company} · {c.industry}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-display font-bold text-accent">{c.metricsValue}</p>
                    <p className="text-[10px] text-muted-foreground">{c.metricsLabel}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section className="py-12 md:py-16 bg-background relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-accent/3 blur-[150px]" />
        </div>
        <div className="container text-center relative">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-semibold tracking-widest uppercase text-accent">{t.pricingPreview.sectionLabel}</span>
            <h2 className="text-2xl md:text-4xl font-display font-bold mt-3 mb-4">{t.pricingPreview.heading}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto mb-6">{t.pricingPreview.subheading}</p>

            {/* Free trial highlight */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-8">
              <Zap className="w-4 h-4 text-accent" />
              <span className="text-sm font-semibold text-accent">免费试用 · 无需信用卡 · 即刻开始</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button variant="hero" size="lg" className="text-base px-10 py-6" asChild>
                <Link to="/dashboard">立即免费试用 <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
              <Button variant="hero-outline" size="lg" className="text-base px-8 py-6" asChild>
                <Link to="/pricing">{t.pricingPreview.cta}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════ FLOATING CTA BAR ══════════ */}
      <AnimatePresence>
        {showFloatingCta && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-t border-border shadow-2xl py-3 px-4 md:px-8"
          >
            <div className="container flex items-center justify-between gap-4">
              <div className="hidden sm:block">
                <p className="text-sm font-display font-bold">准备好了吗？</p>
                <p className="text-xs text-muted-foreground">免费开始使用 DocSeal 智能文档平台</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button variant="hero" size="sm" className="flex-1 sm:flex-none" asChild>
                  <Link to="/dashboard">免费试用 <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
                </Button>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" asChild>
                  <Link to="/pricing">查看定价</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Index;
